import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Gateway from "@/components/Gateway";
import IntroAnimation from "@/components/IntroAnimation";
import TriangleHero from "@/components/TriangleHero";
import KineticMarquee from "@/components/KineticMarquee";
import ClientMarquee from "@/components/ClientMarquee";
import InteractivePortfolio from "@/components/InteractivePortfolio";
import DirectorButton from "@/components/DirectorButton";
import Isotipo from "@/components/Isotipo";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { IMG, PROJECTS, POSTS, BRAIN_LAB, UI } from "@/data/site";
import { SEO, ORG_JSONLD } from "@/data/seo";
import { useLang } from "@/context/LanguageContext";

const Home = () => {
  const { t, lp } = useLang();
  const [phase, setPhase] = useState(() => {
    try { return sessionStorage.getItem("grabarte_entry") ? "site" : "intro"; } catch (e) { return "intro"; }
  });
  const enterSite = () => {
    try { sessionStorage.setItem("grabarte_entry", "1"); } catch (e) {}
    setPhase("site");
  };

  return (
    <div data-testid="home-page">
      <Seo {...SEO.home} jsonLd={ORG_JSONLD} />
      <AnimatePresence>
        {phase !== "site" && <Gateway key="gateway" onClose={enterSite} />}
      </AnimatePresence>
      <AnimatePresence>
        {phase === "intro" && <IntroAnimation key="intro" onDone={() => setPhase("gateway")} />}
      </AnimatePresence>

      <TriangleHero />

      {/* Positioning statement */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <KineticMarquee text="grabarte" className="absolute top-1/2 -translate-y-1/2 inset-x-0 opacity-60" speed={45} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Isotipo className="w-10 h-10 mx-auto mb-8" fill="#edaa51" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black uppercase leading-[1.05] text-white text-balance">
              {t({ es: "Una agencia. Dos disciplinas.", en: "One agency. Two disciplines." })}{" "}
              <span className="text-white/40">
                {t({ es: "Estrategia de marca y cine. 20 años marcando la diferencia.", en: "Brand strategy and cinema. 20 years making the difference." })}
              </span>
            </h2>
          </Reveal>
        </div>
      </section>

      {/* Branch 02 — Audiovisual (star) */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="home-branch-audiovisual">
        <img src={IMG.studio} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 kenburns-slow" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(23,23,38,0.97) 12%, rgba(23,23,38,0.35) 60%, rgba(237,170,81,0.25) 100%)" }} />
        <Isotipo className="absolute top-[12%] right-[6%] w-40 md:w-72 opacity-20" fill="#edaa51" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
          <Reveal>
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">02 — {t({ es: "Estudio de desarrollo", en: "Development studio" })}</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-black uppercase text-white leading-[0.88] tracking-tight" style={{ fontSize: "clamp(2.8rem, 8vw, 8rem)" }}>
              {t({ es: "Producción", en: "Audiovisual" })}<br />{t({ es: "Audiovisual", en: "Production" })}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-white/75 font-body text-lg">
              {t({ es: "Producción propia y coproducción de documentales, películas, series y programas. De la idea al estreno.", en: "In-house production and co-production of documentaries, films, series and programs. From blank page to big screen." })}
            </p>
            <div className="mt-8"><DirectorButton to={lp("/produccion-audiovisual")} color="#edaa51" testId="home-av-cta">{t({ es: "Entrar al estudio", en: "Enter the studio" })}</DirectorButton></div>
          </Reveal>
        </div>
      </section>

      {/* Branch 01 — Marketing (radical green shift) */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#26775e" }} data-testid="home-branch-marketing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-white/70">01 — {t({ es: "La agencia", en: "The agency" })}</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-black uppercase text-white leading-[0.9] tracking-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.8rem)" }}>
                {t({ es: "Marketing, Comunicación & Eventos", en: "Marketing, Communication & Events" })}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-white/85 font-body text-lg">
                {t({ es: "Estrategia, consultoría, contenidos, fidelización y experiencias de marca que generan negocio para las mayores compañías.", en: "Strategy, content, loyalty and brand experiences that turn attention into business for major brands." })}
              </p>
              <div className="mt-8"><DirectorButton to={lp("/marketing-comunicacion-eventos")} variant="solid" color="#ffffff" testId="home-mkt-cta">{t({ es: "Descubrir", en: "Discover" })}</DirectorButton></div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/5] shadow-2xl">
                <img src={IMG.eventos} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-8 -left-8 w-2/3 aspect-video rounded-2xl overflow-hidden border-4 border-brand-green shadow-2xl hidden md:block">
                <img src={IMG.meeting} alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Clients */}
      <section className="border-y border-white/10 py-14 bg-ink">
        <p className="text-center font-display text-xs font-bold uppercase tracking-[0.25em] text-white/40 mb-8">
          {t({ es: "Marcas que confían en nosotros", en: "Brands that trust us" })}
        </p>
        <ClientMarquee />
      </section>

      {/* Interactive portfolio */}
      <InteractivePortfolio projects={PROJECTS} />

      {/* Brain Lab */}
      <section className="relative overflow-hidden py-24 md:py-36 bg-ink-2" data-testid="brain-lab">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">{t({ es: "Cómo trabajamos", en: "How we work" })}</span>
            <h2 className="mt-5 font-display text-4xl md:text-6xl font-black uppercase text-white leading-none">{t(BRAIN_LAB.title)}</h2>
            <p className="mt-6 max-w-xl text-white/60 font-body text-lg leading-relaxed">{t(BRAIN_LAB.desc)}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[16/11] grain">
              <img src={IMG.lightLeak} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Isotipo className="w-24 h-24 md:w-32 md:h-32 opacity-90" fill="#ffffff" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Actualidad preview */}
      <section className="border-t border-white/10 py-24 md:py-32 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-display text-4xl md:text-6xl font-black uppercase text-white">{t(UI.nav.actualidad)}</h2>
              <Link to={lp("/actualidad")} className="hidden md:inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-white/60 hover:text-brand-gold transition-colors">{t(UI.cta.seeMore)}</Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {POSTS.slice(0, 3).map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.1}>
                <article className="group" data-testid={`home-post-${post.slug}`}>
                  <div className="rounded-2xl overflow-hidden aspect-[16/10] mb-5">
                    <img src={post.image} alt={t(post.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <p className="font-display text-xs font-bold uppercase tracking-widest text-brand-gold mb-3">{typeof post.date === "string" ? post.date : t(post.date)}</p>
                  <h3 className="font-display text-lg font-bold text-white leading-snug">{t(post.title)}</h3>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-28 md:py-40 bg-ink">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <Isotipo className="w-12 h-12 mx-auto mb-8" fill="#edaa51" />
            <h2 className="font-display text-4xl md:text-7xl font-black uppercase text-white leading-[0.95] text-balance">
              {t({ es: "¿Marca o historia? Hablemos.", en: "A brand or a story? Let's talk." })}
            </h2>
            <div className="mt-10 flex justify-center">
              <DirectorButton to={lp("/contacto")} color="#edaa51" testId="home-cta-button">{t(UI.cta.talk)}</DirectorButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
